"use client";

import React, { useEffect, useState, useRef } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Mail, ShieldAlert, CircleCheck as CheckCircle2, CircleAlert as AlertCircle, Loader as Loader2 } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
declare const wvcClient: any;
const getWvcClient = () => typeof wvcClient !== "undefined" ? wvcClient : (window as any).wvcClient;
interface FieldOption {
  value: string;
  label: string;
}
interface FieldConfig {
  id: string;
  key: string;
  type: string;
  label: string;
  placeholder?: string;
  required?: boolean;
  help?: string;
  default?: string;
  options?: FieldOption[];
  validation?: {
    messages?: {
      required?: string;
      invalid?: string;
    };
  };
}
interface FormDefinition {
  id: number;
  key: string;
  name: string;
  availability?: {
    accepting: boolean;
    message?: string;
    state?: string;
  };
  fields: FieldConfig[];
  submit?: {
    label?: string;
    processing_label?: string;
  };
  spam?: {
    token: string;
    min_seconds: number;
    honeypot?: {
      field: string;
    } | null;
  };
}
const DEFAULT_REASON_OPTIONS: FieldOption[] = [{
  value: "general_question",
  label: "General Question"
}, {
  value: "product_question",
  label: "Product Question"
}, {
  value: "existing_order",
  label: "Existing Order"
}, {
  value: "parts_compatibility",
  label: "Parts & Compatibility"
}, {
  value: "shipping_delivery",
  label: "Shipping & Delivery"
}, {
  value: "website_question",
  label: "Website Question"
}, {
  value: "other",
  label: "Other"
}];
export default function Contact() {
  const prefersReducedMotion = useReducedMotion();

  // WVC Form states
  const [formDef, setFormDef] = useState<FormDefinition | null>(null);
  const [isLoadingForm, setIsLoadingForm] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Field values and submission states
  const [values, setValues] = useState<Record<string, string>>({});
  const [hpValue, setHpValue] = useState("");
  const [fieldErrors, setFieldErrors] = useState<Record<string, {
    message: string;
  }>>({});
  const [formErrorMessage, setFormErrorMessage] = useState<string | null>(null);
  const [confirmationHtml, setConfirmationHtml] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Spam guard timer
  const fetchTimestampRef = useRef<number>(Date.now());
  const getDefaultValues = (form: FormDefinition | null): Record<string, string> => {
    const defaults: Record<string, string> = {};
    if (form && Array.isArray(form.fields)) {
      form.fields.forEach(field => {
        if (field.type !== "heading" && field.type !== "paragraph" && field.type !== "total" && field.type !== "payment") {
          defaults[field.id] = field.default || "";
        }
      });
    }
    return defaults;
  };
  useEffect(() => {
    let isMounted = true;
    const client = getWvcClient();
    if (!client || typeof client.pluginRequest !== "function") {
      setIsLoadingForm(false);
      setLoadError("Contact form initialization pending. Please email sales@mechanicalbullshop.com directly.");
      return;
    }
    const loadForm = async () => {
      try {
        setIsLoadingForm(true);
        setLoadError(null);
        let res: FormDefinition;
        try {
          res = await client.pluginRequest("wvc-forms/v1/forms/enquiry");
        } catch (err: any) {
          if (err?.status === 404) {
            // Recovery strategy: look up list and match
            const listRes = await client.pluginRequest("wvc-forms/v1/forms");
            const items = listRes?.items || [];
            const matched = items.find((item: any) => item.key === "enquiry") || (items.length === 1 ? items[0] : items.find((item: any) => item.name?.toLowerCase().includes("enquiry") || item.name?.toLowerCase().includes("contact")));
            if (matched && matched.key) {
              res = await client.pluginRequest(`wvc-forms/v1/forms/${matched.key}`);
            } else {
              throw new Error("No compatible contact form schema found.");
            }
          } else {
            throw err;
          }
        }
        if (isMounted && res) {
          setFormDef(res);
          fetchTimestampRef.current = Date.now();
          setValues(getDefaultValues(res));
        }
      } catch (err: any) {
        if (isMounted) {
          setLoadError(err?.message || "Unable to load contact form. Please reach out to us at sales@mechanicalbullshop.com.");
        }
      } finally {
        if (isMounted) {
          setIsLoadingForm(false);
        }
      }
    };
    loadForm();
    return () => {
      isMounted = false;
    };
  }, []);
  const handleInputChange = (fieldId: string, value: string) => {
    if (confirmationHtml) {
      setConfirmationHtml(null);
    }
    setValues(prev => ({
      ...prev,
      [fieldId]: value
    }));
    if (fieldErrors[fieldId]) {
      setFieldErrors(prev => {
        const next = {
          ...prev
        };
        delete next[fieldId];
        return next;
      });
    }
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formDef || isSubmitting) return;
    setFormErrorMessage(null);
    setFieldErrors({});
    setConfirmationHtml(null);

    // Enforce spam minimum seconds client check
    const minSec = formDef.spam?.min_seconds ?? 3;
    const elapsedSec = (Date.now() - fetchTimestampRef.current) / 1000;
    if (elapsedSec < minSec) {
      await new Promise(resolve => setTimeout(resolve, (minSec - elapsedSec + 0.5) * 1000));
    }
    setIsSubmitting(true);
    const client = getWvcClient();
    try {
      const payload: Record<string, any> = {
        values,
        token: formDef.spam?.token,
        meta: {
          source_url: typeof window !== "undefined" ? window.location.href : "",
          referrer: typeof document !== "undefined" ? document.referrer : ""
        }
      };
      if (formDef.spam?.honeypot?.field) {
        payload.hp = {
          [formDef.spam.honeypot.field]: hpValue
        };
      }
      const res = await client.pluginRequest(`wvc-forms/v1/forms/${formDef.key}/submit`, {
        method: "POST",
        data: payload
      });
      if (res?.success) {
        if (res.confirmation?.type === "redirect" && res.confirmation?.url) {
          window.location.assign(res.confirmation.url);
        } else {
          setConfirmationHtml(res.confirmation?.message || "<p>Thank you. Your message has been received and our team will get back to you shortly.</p>");
          setValues(getDefaultValues(formDef));
          setHpValue("");
        }
      } else if (res?.code === "frms_validation_failed") {
        setFieldErrors(res.errors || {});
        setFormErrorMessage(res.message || "Please resolve the highlighted validation errors.");
      } else if (res?.code === "frms_invalid_token") {
        const refreshed: FormDefinition = await client.pluginRequest(`wvc-forms/v1/forms/${formDef.key}`);
        setFormDef(refreshed);
        fetchTimestampRef.current = Date.now();
        setFormErrorMessage("Verification token refreshed. Please submit your form again.");
      } else {
        setFormErrorMessage(res?.message || "Your message could not be submitted. Please try again.");
      }
    } catch (err: any) {
      setFormErrorMessage(err?.message || "There was an error sending your message. Please email us directly at sales@mechanicalbullshop.com.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Field helpers mapping keys to fallback labels if needed
  const getFieldByPlannedKey = (keyName: string, fallbackKeys: string[] = []): FieldConfig | null => {
    if (!formDef || !Array.isArray(formDef.fields)) return null;
    const direct = formDef.fields.find(f => f.key === keyName);
    if (direct) return direct;
    for (const fallbackKey of fallbackKeys) {
      const match = formDef.fields.find(f => f.key === fallbackKey);
      if (match) return match;
    }
    // Fallback virtual field reference if schema doesn't have it explicitly
    return {
      id: `f_${keyName}`,
      key: keyName,
      type: keyName === "reason" ? "select" : keyName === "message" ? "textarea" : "text",
      label: keyName === "full_name" ? "Full Name" : keyName === "email" ? "Email Address" : keyName === "phone" ? "Phone Number" : keyName === "company" ? "Company" : keyName === "reason" ? "Reason for Inquiry" : keyName === "product_reference" ? "Product Name or SKU" : keyName === "order_number" ? "Order / Enquiry Number" : "Message",
      required: keyName === "full_name" || keyName === "email" || keyName === "message"
    };
  };
  const fullNameField = getFieldByPlannedKey("full_name", ["name"]);
  const emailField = getFieldByPlannedKey("email", ["email_address"]);
  const phoneField = getFieldByPlannedKey("phone", ["phone_number"]);
  const companyField = getFieldByPlannedKey("company", ["business_name", "organization"]);
  const reasonField = getFieldByPlannedKey("reason", ["inquiry_reason", "subject"]);
  const productReferenceField = getFieldByPlannedKey("product_reference", ["product_name", "product_sku", "sku"]);
  const orderNumberField = getFieldByPlannedKey("order_number", ["order_id", "enquiry_number", "order_ref"]);
  const messageField = getFieldByPlannedKey("message", ["project_message", "comments"]);
  const renderedMappedFieldIds = new Set([fullNameField?.id, emailField?.id, phoneField?.id, companyField?.id, reasonField?.id, productReferenceField?.id, orderNumberField?.id, messageField?.id].filter(Boolean));
  const additionalFields = formDef?.fields?.filter(f => !renderedMappedFieldIds.has(f.id) && f.type !== "heading" && f.type !== "paragraph" && f.type !== "hidden") || [];
  const reasonOptions = reasonField?.options && reasonField.options.length > 0 ? reasonField.options : DEFAULT_REASON_OPTIONS;
  return <section id="contact" data-nav="light" className="relative bg-background text-foreground py-16 md:py-24 border-t border-border">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12">
        {/* Section Header */}
        <div className="mb-12 md:mb-16 border-b border-border pb-8">
          <div className="inline-flex items-center gap-2 border border-border bg-muted px-3 py-1 text-xs font-mono tracking-wider uppercase text-muted-foreground mb-4">
            <span className="inline-block size-2 bg-primary"></span>
            CONTACT MECHANICAL BULL SHOP
          </div>
          <h1 className="font-serif text-3xl sm:text-4xl md:text-5xl font-bold uppercase tracking-tight text-foreground">
            Contact Mechanical Bull Shop
          </h1>
          <p className="mt-4 max-w-3xl text-base sm:text-lg text-muted-foreground font-sans leading-relaxed">
            Have a question about a mechanical bull, an existing order, replacement parts, shipping or something else? Send us a message and include as much relevant information as you can. That helps us understand what you need and respond with the right information.
          </p>
        </div>

        {/* Two-Column Split Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14">
          {/* Left Column: Email Details & Contact Options */}
          <motion.div initial={prefersReducedMotion ? false : {
          opacity: 0,
          y: 12
        }} whileInView={prefersReducedMotion ? undefined : {
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.3,
          ease: [0.16, 1, 0.3, 1]
        }} className="lg:col-span-5 flex flex-col space-y-6">
            {/* Prominent Email Card */}
            <div className="border border-border p-6 bg-card">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2.5 border border-border bg-muted text-foreground">
                  <Mail className="size-5 text-primary" />
                </div>
                <div>
                  <span className="block font-mono text-xs text-muted-foreground uppercase tracking-wider">
                    Direct Email
                  </span>
                  <h2 className="font-serif text-lg font-bold uppercase text-foreground">
                    Email Us Directly
                  </h2>
                </div>
              </div>
              <p className="text-sm text-muted-foreground mb-4">
                Send your questions, unit inquiries, part requests, or order details directly to our inbox.
              </p>
              <a href="mailto:sales@mechanicalbullshop.com" className="font-mono text-lg font-semibold text-primary hover:underline break-all">
                sales@mechanicalbullshop.com
              </a>
            </div>

            {/* Simple Contact Options Block */}
            <div className="border border-border p-6 bg-card">
              <div className="border-b border-border pb-3 mb-4">
                <span className="font-mono text-xs uppercase tracking-wider text-muted-foreground">
                  Inquiry Guide
                </span>
                <h3 className="font-serif text-xl font-bold uppercase text-foreground">
                  Contact Options
                </h3>
              </div>
              <ul className="space-y-4 font-sans text-sm">
                <li className="border-b border-border pb-3">
                  <span className="font-serif font-bold uppercase tracking-wide text-foreground block text-base">
                    General Questions
                  </span>
                  <p className="text-muted-foreground text-sm mt-0.5">
                    About the website, available equipment or Mechanical Bull Shop.
                  </p>
                </li>
                <li className="border-b border-border pb-3">
                  <span className="font-serif font-bold uppercase tracking-wide text-foreground block text-base">
                    Product Questions
                  </span>
                  <p className="text-muted-foreground text-sm mt-0.5">
                    About a specific bull, used unit, part or accessory.
                  </p>
                </li>
                <li className="border-b border-border pb-3">
                  <span className="font-serif font-bold uppercase tracking-wide text-foreground block text-base">
                    Existing Orders
                  </span>
                  <p className="text-muted-foreground text-sm mt-0.5">
                    About an order or enquiry already in progress.
                  </p>
                </li>
                <li className="border-b border-border pb-3">
                  <span className="font-serif font-bold uppercase tracking-wide text-foreground block text-base">
                    Parts & Compatibility
                  </span>
                  <p className="text-muted-foreground text-sm mt-0.5">
                    Identifying or matching a replacement component.
                  </p>
                </li>
                <li>
                  <span className="font-serif font-bold uppercase tracking-wide text-foreground block text-base">
                    Shipping & Delivery
                  </span>
                  <p className="text-muted-foreground text-sm mt-0.5">
                    A general delivery/receiving question.
                  </p>
                </li>
              </ul>
            </div>
          </motion.div>

          {/* Right Column: Contact Message Form */}
          <motion.div initial={prefersReducedMotion ? false : {
          opacity: 0,
          y: 12
        }} whileInView={prefersReducedMotion ? undefined : {
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.3,
          ease: [0.16, 1, 0.3, 1],
          delay: prefersReducedMotion ? 0 : 0.05
        }} className="lg:col-span-7">
            <div className="border border-border bg-card p-6 sm:p-8 lg:p-10 shadow-xs">
              <div className="border-b border-border pb-6 mb-6">
                <span className="font-mono text-xs uppercase tracking-wider text-primary font-semibold">
                  Online Inquiry
                </span>
                <h2 className="font-serif text-2xl sm:text-3xl font-bold uppercase text-foreground mt-1">
                  Send Us a Message
                </h2>
                <p className="text-sm text-muted-foreground mt-2">
                  Fill in the details below and we will get back to you with the answers or quote details you need.
                </p>
              </div>

              {isLoadingForm && <div className="py-16 flex flex-col items-center justify-center text-center space-y-3">
                  <Loader2 className="size-8 animate-spin text-primary" />
                  <p className="font-mono text-sm text-muted-foreground uppercase">
                    Loading contact form...
                  </p>
                </div>}

              {!isLoadingForm && loadError && <div className="border border-destructive/30 bg-destructive/5 p-6 text-foreground space-y-3">
                  <div className="flex items-center gap-2 text-destructive font-semibold">
                    <AlertCircle className="size-5" />
                    <span>Contact Notice</span>
                  </div>
                  <p className="text-sm text-muted-foreground">{loadError}</p>
                  <p className="text-sm">
                    Please email us directly at{" "}
                    <a href="mailto:sales@mechanicalbullshop.com" className="font-mono font-bold underline text-primary">
                      sales@mechanicalbullshop.com
                    </a>
                    .
                  </p>
                </div>}

              {!isLoadingForm && !loadError && <>
                  {confirmationHtml && <div className="border border-border bg-muted p-8 text-center space-y-4 mb-6">
                      <div className="mx-auto size-12 border border-primary bg-background flex items-center justify-center text-primary">
                        <CheckCircle2 className="size-6" />
                      </div>
                      <h3 className="font-serif text-2xl font-bold uppercase text-foreground">
                        Message Sent
                      </h3>
                      <div className="text-sm text-muted-foreground leading-relaxed [&_p]:mb-2" dangerouslySetInnerHTML={{
                  __html: confirmationHtml
                }} />
                    </div>}

                  <form onSubmit={handleSubmit} className="space-y-5">
                    {/* Honeypot trap field (visually hidden) */}
                    {formDef?.spam?.honeypot?.field && <div style={{
                  position: "absolute",
                  left: "-9999px",
                  top: "-9999px",
                  opacity: 0,
                  pointerEvents: "none"
                }} aria-hidden="true">
                        <label htmlFor={formDef.spam.honeypot.field}>Do not fill this field</label>
                        <input type="text" id={formDef.spam.honeypot.field} name={formDef.spam.honeypot.field} tabIndex={-1} autoComplete="off" value={hpValue} onChange={e => setHpValue(e.target.value)} />
                      </div>}

                    {formErrorMessage && <div className="border border-destructive bg-destructive/10 p-4 text-sm text-destructive flex items-start gap-2.5">
                        <ShieldAlert className="size-4 shrink-0 mt-0.5" />
                        <div>{formErrorMessage}</div>
                      </div>}

                    {/* Form Grid */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      {/* Full Name */}
                      {fullNameField && <div className="space-y-1.5 sm:col-span-1">
                          <label htmlFor={fullNameField.id} className="block text-xs font-mono uppercase tracking-wider text-foreground font-semibold">
                            Full Name {fullNameField.required && <span className="text-destructive">*</span>}
                          </label>
                          <Input id={fullNameField.id} type="text" required={fullNameField.required} placeholder={fullNameField.placeholder || "John Doe"} value={values[fullNameField.id] || ""} onChange={e => handleInputChange(fullNameField.id, e.target.value)} className={`rounded-none border-input focus-visible:border-foreground focus-visible:ring-0 ${fieldErrors[fullNameField.id] ? "border-destructive ring-1 ring-destructive" : ""}`} />
                          {fieldErrors[fullNameField.id] && <p className="text-xs text-destructive font-mono mt-1">
                              {fieldErrors[fullNameField.id].message}
                            </p>}
                        </div>}

                      {/* Email Address */}
                      {emailField && <div className="space-y-1.5 sm:col-span-1">
                          <label htmlFor={emailField.id} className="block text-xs font-mono uppercase tracking-wider text-foreground font-semibold">
                            Email Address {emailField.required && <span className="text-destructive">*</span>}
                          </label>
                          <Input id={emailField.id} type="email" required={emailField.required} placeholder={emailField.placeholder || "you@example.com"} value={values[emailField.id] || ""} onChange={e => handleInputChange(emailField.id, e.target.value)} className={`rounded-none border-input focus-visible:border-foreground focus-visible:ring-0 ${fieldErrors[emailField.id] ? "border-destructive ring-1 ring-destructive" : ""}`} />
                          {fieldErrors[emailField.id] && <p className="text-xs text-destructive font-mono mt-1">
                              {fieldErrors[emailField.id].message}
                            </p>}
                        </div>}

                      {/* Phone Number */}
                      {phoneField && <div className="space-y-1.5 sm:col-span-1">
                          <label htmlFor={phoneField.id} className="block text-xs font-mono uppercase tracking-wider text-foreground font-semibold">
                            Phone Number {phoneField.required && <span className="text-destructive">*</span>}
                          </label>
                          <Input id={phoneField.id} type="tel" required={phoneField.required} placeholder={phoneField.placeholder || "(555) 000-0000"} value={values[phoneField.id] || ""} onChange={e => handleInputChange(phoneField.id, e.target.value)} className={`rounded-none border-input focus-visible:border-foreground focus-visible:ring-0 ${fieldErrors[phoneField.id] ? "border-destructive ring-1 ring-destructive" : ""}`} />
                          {fieldErrors[phoneField.id] && <p className="text-xs text-destructive font-mono mt-1">
                              {fieldErrors[phoneField.id].message}
                            </p>}
                        </div>}

                      {/* Company */}
                      {companyField && <div className="space-y-1.5 sm:col-span-1">
                          <label htmlFor={companyField.id} className="block text-xs font-mono uppercase tracking-wider text-foreground font-semibold">
                            Company {companyField.required && <span className="text-destructive">*</span>}
                          </label>
                          <Input id={companyField.id} type="text" required={companyField.required} placeholder={companyField.placeholder || "Company or Business Name"} value={values[companyField.id] || ""} onChange={e => handleInputChange(companyField.id, e.target.value)} className={`rounded-none border-input focus-visible:border-foreground focus-visible:ring-0 ${fieldErrors[companyField.id] ? "border-destructive ring-1 ring-destructive" : ""}`} />
                          {fieldErrors[companyField.id] && <p className="text-xs text-destructive font-mono mt-1">
                              {fieldErrors[companyField.id].message}
                            </p>}
                        </div>}

                      {/* Reason */}
                      {reasonField && <div className="space-y-1.5 sm:col-span-2">
                          <label htmlFor={reasonField.id} className="block text-xs font-mono uppercase tracking-wider text-foreground font-semibold">
                            Reason for Inquiry {reasonField.required && <span className="text-destructive">*</span>}
                          </label>
                          <select id={reasonField.id} required={reasonField.required} value={values[reasonField.id] || ""} onChange={e => handleInputChange(reasonField.id, e.target.value)} className={`w-full rounded-none border border-input bg-background px-3 py-2 text-sm text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring ${fieldErrors[reasonField.id] ? "border-destructive ring-1 ring-destructive" : ""}`}>
                            <option value="">Select a reason...</option>
                            {reasonOptions.map(opt => <option key={opt.value} value={opt.value}>
                                {opt.label}
                              </option>)}
                          </select>
                          {fieldErrors[reasonField.id] && <p className="text-xs text-destructive font-mono mt-1">
                              {fieldErrors[reasonField.id].message}
                            </p>}
                        </div>}

                      {/* Product Reference */}
                      {productReferenceField && <div className="space-y-1.5 sm:col-span-1">
                          <label htmlFor={productReferenceField.id} className="block text-xs font-mono uppercase tracking-wider text-foreground font-semibold">
                            Product Name or SKU {productReferenceField.required && <span className="text-destructive">*</span>}
                          </label>
                          <Input id={productReferenceField.id} type="text" required={productReferenceField.required} placeholder={productReferenceField.placeholder || "e.g. Mechanical Bull Model or Part SKU"} value={values[productReferenceField.id] || ""} onChange={e => handleInputChange(productReferenceField.id, e.target.value)} className={`rounded-none border-input focus-visible:border-foreground focus-visible:ring-0 ${fieldErrors[productReferenceField.id] ? "border-destructive ring-1 ring-destructive" : ""}`} />
                          {fieldErrors[productReferenceField.id] && <p className="text-xs text-destructive font-mono mt-1">
                              {fieldErrors[productReferenceField.id].message}
                            </p>}
                        </div>}

                      {/* Order Number */}
                      {orderNumberField && <div className="space-y-1.5 sm:col-span-1">
                          <label htmlFor={orderNumberField.id} className="block text-xs font-mono uppercase tracking-wider text-foreground font-semibold">
                            Order / Enquiry Number {orderNumberField.required && <span className="text-destructive">*</span>}
                          </label>
                          <Input id={orderNumberField.id} type="text" required={orderNumberField.required} placeholder={orderNumberField.placeholder || "e.g. ORD-10482"} value={values[orderNumberField.id] || ""} onChange={e => handleInputChange(orderNumberField.id, e.target.value)} className={`rounded-none border-input focus-visible:border-foreground focus-visible:ring-0 ${fieldErrors[orderNumberField.id] ? "border-destructive ring-1 ring-destructive" : ""}`} />
                          {fieldErrors[orderNumberField.id] && <p className="text-xs text-destructive font-mono mt-1">
                              {fieldErrors[orderNumberField.id].message}
                            </p>}
                        </div>}

                      {/* Message */}
                      {messageField && <div className="space-y-1.5 sm:col-span-2">
                          <label htmlFor={messageField.id} className="block text-xs font-mono uppercase tracking-wider text-foreground font-semibold">
                            Message {messageField.required && <span className="text-destructive">*</span>}
                          </label>
                          <Textarea id={messageField.id} rows={4} required={messageField.required} placeholder={messageField.placeholder || "How can we help? Include any specific details, questions, or requirements."} value={values[messageField.id] || ""} onChange={e => handleInputChange(messageField.id, e.target.value)} className={`rounded-none border-input focus-visible:border-foreground focus-visible:ring-0 ${fieldErrors[messageField.id] ? "border-destructive ring-1 ring-destructive" : ""}`} />
                          {fieldErrors[messageField.id] && <p className="text-xs text-destructive font-mono mt-1">
                              {fieldErrors[messageField.id].message}
                            </p>}
                        </div>}

                      {/* Additional schema fields fallback if form contains extra entries */}
                      {additionalFields.map((field, i) => <div key={field.id} data-index={i} className="space-y-1.5 sm:col-span-2">
                          <label htmlFor={field.id} className="block text-xs font-mono uppercase tracking-wider text-foreground font-semibold">
                            {field.label} {field.required && <span className="text-destructive">*</span>}
                          </label>
                          {field.type === "textarea" ? <Textarea id={field.id} required={field.required} placeholder={field.placeholder || ""} value={values[field.id] || ""} onChange={e => handleInputChange(field.id, e.target.value)} className="rounded-none border-input focus-visible:border-foreground focus-visible:ring-0" /> : <Input id={field.id} type={field.type === "number" ? "number" : "text"} required={field.required} placeholder={field.placeholder || ""} value={values[field.id] || ""} onChange={e => handleInputChange(field.id, e.target.value)} className="rounded-none border-input focus-visible:border-foreground focus-visible:ring-0" />}
                          {fieldErrors[field.id] && <p className="text-xs text-destructive font-mono mt-1">
                              {fieldErrors[field.id].message}
                            </p>}
                        </div>)}
                    </div>

                    {/* Submission Row */}
                    <div className="pt-3 border-t border-border flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                      <p className="text-xs text-muted-foreground font-sans">
                        We will review your inquiry and get back to you promptly.
                      </p>
                      <Button type="submit" disabled={isSubmitting} className="w-full sm:w-auto bg-primary text-primary-foreground hover:bg-primary/90 rounded-none h-11 px-8 font-serif uppercase tracking-wider font-semibold text-base shrink-0 transition-[color,background-color,border-color]">
                        {isSubmitting ? <>
                            <Loader2 className="size-4 animate-spin mr-2" />
                            SENDING...
                          </> : "SEND MESSAGE"}
                      </Button>
                    </div>
                  </form>
                </>}
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}